import { h, Fragment } from 'preact';
import { useMemo } from 'preact/hooks';
import clsx from 'clsx';
import { formatMoney } from 'utils/storefront/format-resource';
import ProductSwatch from './product-swatch';
import useDiscountProduct from '../../product-discounts/product-discount';

const ProductMainSwatches = ({
  products,
  options,
  currentProduct,
  onChange,
  isQuickView,
  isLookOverlay,
  template,
}) => {
  const isGiftCard = template === 'product.gift-card';
  const inlineDiscount = useDiscountProduct(
    currentProduct.id,
    currentProduct.priceRange.minVariantPrice.amount,
    currentProduct.collectionsId,
  );
  const productGroups = useMemo(
    () =>
      products.reduce((groups, product) => {
        const item = { ...groups };
        const discount = window.getProductDiscount(
          product.id,
          product.priceRange.minVariantPrice.amount,
          product.collectionsId,
        );
        const group =
          discount?.discountPrice?.amount ||
          product.priceRange.minVariantPrice.amount;
        if (!item[group]) {
          item[group] = {
            items: [],
            price:
              discount?.discountPrice || product.priceRange.minVariantPrice,
            label: null,
            compareAt: {
              amount: 0,
            },
          };
        }

        if (currentProduct.id === product.id) {
          // First check for color
          const colorOption = product.options.find(
            (option) =>
              option.name.trim().toLowerCase() ===
              window.TMW.Strings.product.options.color,
          );

          // If color exists, use it
          if (colorOption) {
            const [colorValue] = colorOption.values;
            item[group].label = colorValue;
          } else {
            // Otherwise check for design
            const designOption = product.options.find(
              (option) =>
                option.name.trim().toLowerCase() ===
                window.TMW.Strings.product.options.design,
            );

            // Use design value if it exists
            if (designOption) {
              const [designValue] = designOption.values;
              item[group].label = designValue;
            }
          }
        }

        if (
          product.compareAtPriceRange.maxVariantPrice.amount >
          item[group].compareAt.amount
        ) {
          item[group].compareAt = product.compareAtPriceRange.maxVariantPrice;
        }
        item[group].items.push(product);
        return item;
      }, {}),
    [products, currentProduct, inlineDiscount],
  );

  return (
    <div
      className={clsx('w-full', {
        'px-24': isQuickView,
      })}
    >
      <div
        className={clsx('pb-16 flex flex-col gap-24 ml:lg:gap-26', {
          'pt-16 lc:lg:py-20': !isQuickView && !isLookOverlay,
          'lc:pb-24': isLookOverlay,
        })}
      >
        {Object.keys(productGroups)
          .sort((a, b) => b - a)
          .map((group) => (
            <div
              className={clsx({
                'gap-y-10 flex flex-col': isQuickView,
              })}
              key={group}
            >
              <div
                className={clsx('flex gap-8 items-end', {
                  'mb-8': !isQuickView && !isLookOverlay,
                  'mb-10': isLookOverlay,
                  'ml:justify-center': !isLookOverlay || isGiftCard
                })}
              >
                {Object.keys(productGroups).length > 1 && (
                  <>
                    <div
                      className={clsx({
                        'body-2 !leading-100': !isQuickView,
                        'body-1 ml:body-2 !leading-100': isQuickView,
                      })}
                    >
                      {formatMoney(productGroups[group].price, false)}
                    </div>
                    {productGroups[group].compareAt.amount >
                      productGroups[group].price.amount && (
                      <div className="body-3 line-through text-subdued !leading-100">
                        {formatMoney(productGroups[group].compareAt, false)}
                      </div>
                    )}
                  </>
                )}
                {!!productGroups[group].label && (
                  <div className="body-3 !leading-100">
                    {template === 'product.gift-card' ? (
                      <>
                        <span className="text-default text-[0.75rem]">
                          {window.TMW.Strings.giftCard.recipient.chooseDesign}{' '}
                        </span>
                        <span className="text-subdued text-[0.75rem]">
                          {productGroups[group].label}
                        </span>
                      </>
                    ) : (
                      <span className="text-subdued">
                        {productGroups[group].label}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div
                className={clsx('flex items-center flex-wrap', {
                  'gap-5 product-main-swatches ml:gap-y-12': !isQuickView,
                  'gap-8': isQuickView || isLookOverlay,
                  'ml:justify-center': !isLookOverlay && !isGiftCard,
                  'ml:justify-start': isGiftCard,
                })}
              >
                {productGroups[group].items.map((product) => (
                  <ProductSwatch
                    key={product.id}
                    onChange={onChange}
                    options={options}
                    product={product}
                    isQuickView={isQuickView}
                    isLookOverlay={isLookOverlay}
                    selected={currentProduct.id === product.id}
                    template={template}
                  />
                ))}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};
export default ProductMainSwatches;
